import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Image from 'next/image';

import { Header } from '../components/Header';
import { PageMetadata } from '../components/PageMetadata';
import { Hero } from '../components/Hero';
import { heroData } from '../components/heroData';
import { Footer } from '../components/Footer/Footer';
import { Section, Row, Column } from '../components/Section';
import BlueOptionsCopy from '../copy/group/blue-options.md';
import BlueLocalCopy from '../copy/group/blue-local.md';
import BlueValueCopy from '../copy/group/blue-value.md';
import BlueSelectCopy from '../copy/group/blue-select.md';
import FootnotesCopy from '../copy/group/footnotes.md';
import { CallToActionSection } from '../components/CallToActionSection';
import blueOptionsLogo from '../public/static/images/blue-options.svg';
import blueLocalLogo from '../public/static/images/blue-local.svg';
import blueValueLogo from '../public/static/images/blue-value.svg';
import blueSelectLogo from '../public/static/images/blue-select.svg';
import { CommonQuestions } from '../components/CommonQuestions/CommonQuestions';
import { GetServerPageProps } from '../lib/apollo';
import { getServerPageGroup } from './Group.generated';

export default function Group({
  pageMetadata,
  heroData,
  commonQuestionsSnippet,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <>
      <PageMetadata
        pageTitle={pageMetadata?.seoTitle ?? undefined}
        description={pageMetadata?.seoDescription ?? undefined}
        openGraphImage={pageMetadata?.openGraphImage?.url ?? undefined}
      />
      <Header planType="group" />
      <Hero
        heading={heroData.heading}
        subheading={heroData.subheading}
        image={{
          url: pageMetadata?.heroImage?.url ?? '',
          description: pageMetadata?.heroImage?.description ?? '',
        }}
        aggregatedReviews={heroData.aggregatedReviews}
        defaultPlanTypeOption="group"
      />
      <div className="w-full relative lg:pt-8 bg-gray-200">
        <div className="container mx-auto mb-6 px-4 flex flex-wrap items-start justify-center">
          <Image
            src={blueOptionsLogo}
            className="mx-4 my-3"
            alt="Blue Options"
          />
          <Image src={blueLocalLogo} className="mx-4 my-3" alt="Blue Local" />
          <Image src={blueValueLogo} className="mx-4 my-3" alt="Blue Value" />
          <Image src={blueSelectLogo} className="mx-4 my-3" alt="Blue Select" />
        </div>
        <CallToActionSection quoteAction />
        <Section background="WHITE">
          <Row inset={true}>
            <Column>
              <Image
                src={blueOptionsLogo}
                alt="Blue Options"
                className="mx-auto"
                height={40}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <BlueOptionsCopy />
            </Column>
          </Row>
        </Section>
        <Section background="GRAY">
          <Row inset={true}>
            <Column>
              <Image
                src={blueLocalLogo}
                alt="Blue Local"
                className="mx-auto"
                height={53}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <BlueLocalCopy />
            </Column>
          </Row>
        </Section>
        <Section background="WHITE">
          <Row inset={true}>
            <Column>
              <Image
                src={blueValueLogo}
                alt="Blue Value"
                className="mx-auto"
                height={40}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <BlueValueCopy />
            </Column>
          </Row>
        </Section>
        <Section background="GRAY">
          <Row inset={true}>
            <Column>
              <Image
                src={blueSelectLogo}
                alt="Blue Select"
                className="mx-auto"
                height={40}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <BlueSelectCopy />
            </Column>
          </Row>
        </Section>
        <CommonQuestions snippetGroup={commonQuestionsSnippet} inset />
        <div className="bg-gray-300">
          <div className="container mx-auto text-xs py-6 px-4 lg:px-6">
            <FootnotesCopy />
          </div>
        </div>
        <CallToActionSection planType="group" quoteAction />
        <Footer />
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  pageMetadata:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageGroup>['data']
        >['pageCollection']
      >['items'][0]
    | undefined;
  heroData: Awaited<ReturnType<typeof heroData>>;
  commonQuestionsSnippet:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageGroup>['data']
        >['snippetGroupCollection']
      >['items'][0]
    | undefined;
}> = async () => {
  const { data } = (await getServerPageGroup({})).props;

  return {
    props: {
      pageMetadata: data.pageCollection?.items[0],
      heroData: await heroData(),
      commonQuestionsSnippet: data.snippetGroupCollection?.items[0],
    },
  };
};
